export type ChatTreeNode = {
  id: string;
  parentId: string | null;
  createdAt: string;
};

function cmpNodes(a: ChatTreeNode, b: ChatTreeNode): number {
  const byTime = a.createdAt.localeCompare(b.createdAt);
  if (byTime !== 0) return byTime;
  return a.id.localeCompare(b.id);
}

function childrenMap(nodes: ChatTreeNode[]): Map<string | null, ChatTreeNode[]> {
  const map = new Map<string | null, ChatTreeNode[]>();
  for (const node of nodes) {
    const key = node.parentId;
    const list = map.get(key);
    if (list) list.push(node);
    else map.set(key, [node]);
  }
  for (const list of map.values()) list.sort(cmpNodes);
  return map;
}

export function childrenOf(nodes: ChatTreeNode[], parentId: string | null): ChatTreeNode[] {
  return childrenMap(nodes).get(parentId) ?? [];
}

export function pathFromRoot(nodes: ChatTreeNode[], leafId: string | null): ChatTreeNode[] {
  if (!leafId) return [];
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const path: ChatTreeNode[] = [];
  const seen = new Set<string>();
  let current: ChatTreeNode | undefined = byId.get(leafId);
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    path.push(current);
    current = current.parentId ? byId.get(current.parentId) : undefined;
  }
  path.reverse();
  return path;
}

const PERSISTED_MESSAGE_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function isPersistedMessageId(id: string | null | undefined): boolean {
  return Boolean(id && PERSISTED_MESSAGE_ID_RE.test(id));
}

/** Follow temp `u_` / `a_` ids to the UUID written by persist / loadHistory. */
export function resolvePersistedMessageId(
  rawId: string | null | undefined,
  aliases: ReadonlyMap<string, string>,
): string | null {
  if (rawId == null || rawId === "") return null;
  const seen = new Set<string>();
  let current = rawId;
  while (current && !seen.has(current)) {
    seen.add(current);
    if (PERSISTED_MESSAGE_ID_RE.test(current)) return current;
    const next = aliases.get(current);
    if (!next) return null;
    current = next;
  }
  return null;
}

export function rememberMessageIdAliases(
  previous: Array<{ id: string; role: string; requestId?: string | null }>,
  next: Array<{ id: string; role: string; requestId?: string | null }>,
  aliases: Map<string, string>,
): void {
  for (const old of previous) {
    if (PERSISTED_MESSAGE_ID_RE.test(old.id) || !old.requestId) continue;
    const hit = next.find(
      (row) =>
        row.requestId === old.requestId
        && row.role === old.role
        && PERSISTED_MESSAGE_ID_RE.test(row.id),
    );
    if (hit) aliases.set(old.id, hit.id);
  }
}

export function rememberOptimisticTurnAliases(
  aliases: Map<string, string>,
  optimistic: { userId: string; assistantId: string },
  persistedPath: Array<{ id: string; role: string }>,
): void {
  const lastUser = [...persistedPath].reverse().find((row) => row.role === "user");
  const lastAssistant = [...persistedPath].reverse().find((row) => row.role === "assistant");
  if (lastUser && PERSISTED_MESSAGE_ID_RE.test(lastUser.id)) {
    aliases.set(optimistic.userId, lastUser.id);
  }
  if (lastAssistant && PERSISTED_MESSAGE_ID_RE.test(lastAssistant.id)) {
    aliases.set(optimistic.assistantId, lastAssistant.id);
  }
}

export function pathIdSet(nodes: ChatTreeNode[], leafId: string | null): Set<string> {
  return new Set(pathFromRoot(nodes, leafId).map((node) => node.id));
}

function newestIn(nodes: ChatTreeNode[]): ChatTreeNode | null {
  if (nodes.length === 0) return null;
  return [...nodes].sort(cmpNodes)[nodes.length - 1] ?? null;
}

export function resolveActiveLeaf(nodes: ChatTreeNode[], storedLeafId: string | null): string | null {
  if (storedLeafId && nodes.some((node) => node.id === storedLeafId)) return storedLeafId;
  return newestIn(nodes)?.id ?? null;
}

export function newestLeafInSubtree(nodes: ChatTreeNode[], nodeId: string): string | null {
  const byParent = childrenMap(nodes);
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const root = byId.get(nodeId);
  if (!root) return null;
  const stack: ChatTreeNode[] = [root];
  const seen = new Set<string>();
  const subtree: ChatTreeNode[] = [];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node || seen.has(node.id)) continue;
    seen.add(node.id);
    subtree.push(node);
    for (const child of byParent.get(node.id) ?? []) stack.push(child);
  }
  return newestIn(subtree)?.id ?? nodeId;
}

export function leafUnderNode(
  nodes: ChatTreeNode[],
  nodeId: string,
  preferredLeafId: string | null,
): string | null {
  if (!nodes.some((node) => node.id === nodeId)) return null;
  if (preferredLeafId && (preferredLeafId === nodeId || pathIdSet(nodes, preferredLeafId).has(nodeId))) {
    return preferredLeafId;
  }
  return newestLeafInSubtree(nodes, nodeId);
}

/**
 * Visible chat after picking a version: shared prefix + that node + its follow-ups.
 * Never the subtree alone (that would drop history before the fork).
 */
export function visiblePathAfterSelect(
  nodes: ChatTreeNode[],
  selectedId: string,
  preferredLeafId: string | null,
): ChatTreeNode[] {
  return pathFromRoot(nodes, leafUnderNode(nodes, selectedId, preferredLeafId));
}

export type SiblingMeta = {
  index: number;
  total: number;
  siblingIds: string[];
};

export function siblingMeta(nodes: ChatTreeNode[], nodeId: string): SiblingMeta | null {
  const node = nodes.find((row) => row.id === nodeId);
  if (!node) return null;
  const siblings = childrenOf(nodes, node.parentId);
  const index = siblings.findIndex((row) => row.id === nodeId);
  if (index < 0) return { index: 1, total: 1, siblingIds: [nodeId] };
  return {
    index: index + 1,
    total: siblings.length,
    siblingIds: siblings.map((row) => row.id),
  };
}

/** `siblingMeta` für alle Knoten auf einmal — O(n log n) statt O(n²) bei Aufruf pro Zeile. */
export function siblingMetaById(nodes: ChatTreeNode[]): Map<string, SiblingMeta> {
  const out = new Map<string, SiblingMeta>();
  for (const siblings of childrenMap(nodes).values()) {
    const siblingIds = siblings.map((row) => row.id);
    siblings.forEach((row, i) => out.set(row.id, { index: i + 1, total: siblings.length, siblingIds }));
  }
  return out;
}
